import React, { useEffect, useState } from 'react';

import Welcome from './Welcome/Welcome';
import { useAppSelector } from '../../../app/hooks';
import { selectFailedGmailConnection } from '../../../features/ui/uiSlice';
import MissingScopes from './MissingScopes/MissingScopes';
import ConnectGmailPrompt from '../../ConnectGmailPrompt/ConnectGmailPrompt';
import { useLiveQuery } from 'dexie-react-hooks';
import { useGetMailboxData } from '../../../app/useGetMailboxData';
import Mailbox, { getCurrentMailboxQuery } from '../../../model/mailbox';
import { LandingPageFlavorEnum } from './Welcome/welcomeFlavors.types';
import { EventName } from '../../../services/analytics/providers/analyticsProvider';
import AnalyticsService from '../../../services/analytics/analyticsService';
import { useHasCompletedOnboardingSlideshow } from '../../../app/useHasCompletedOnboardingSlideshow';
import OnboardingSlideshow from './OnboardingSlideshow/OnboardingSlideshow';
import { useNavigate } from 'react-router-dom';
import SignIn from './SignIn/SignIn';
import { usePaywall } from '../../../providers/PaywallProvider';

export type OnboardingFlowWithSlideshowProps = {
  landingPageFlavor: LandingPageFlavorEnum;
};

function OnboardingFlowWithSlideshow(props: OnboardingFlowWithSlideshowProps) {
  const { landingPageFlavor } = props;

  const [onboardingStep, setOnboardingStep] = useState<'WELCOME' | 'SLIDESHOW' | 'SIGN_IN'>(
    'WELCOME'
  );

  const failedGmailConnection = useAppSelector(selectFailedGmailConnection);

  const mailbox: Mailbox | undefined | null = useLiveQuery(getCurrentMailboxQuery);
  const { hasTokenAndUserInfoScopes, hasTokenAndFullScopes } = useGetMailboxData(mailbox);

  const { hasCompletedOnboardingSlideshow } = useHasCompletedOnboardingSlideshow();

  const { hasPremium, isLoadingPremiumStatus } = usePaywall();

  const navigate = useNavigate();

  useEffect(() => {
    if (hasTokenAndFullScopes && mailbox?.email_address && !hasPremium) {
      navigate(`/upgrade?email=${mailbox?.email_address}`);
    } else if (onboardingStep === 'WELCOME' && hasTokenAndUserInfoScopes) {
      setOnboardingStep('SIGN_IN');
    }
  }, [
    hasTokenAndUserInfoScopes,
    hasTokenAndFullScopes,
    hasPremium,
    onboardingStep,
    isLoadingPremiumStatus,
  ]);

  const firstName = mailbox?.first_name;
  const email = mailbox?.email_address;

  useEffect(() => {
    if (onboardingStep === 'WELCOME') {
      AnalyticsService.track(EventName.GET_STARTED_VISIT);
    } else if (onboardingStep === 'SLIDESHOW') {
      AnalyticsService.track(EventName.ONBOARDING_SLIDESHOW_VISIT);
    } else if (onboardingStep === 'SIGN_IN') {
      AnalyticsService.track(EventName.VIEW_PROVIDERS_VISIT);
    }
  }, [onboardingStep]);

  if (onboardingStep === 'WELCOME') {
    return (
      <Welcome
        next={() => {
          if (hasCompletedOnboardingSlideshow) {
            setOnboardingStep('SIGN_IN');
          } else {
            setOnboardingStep('SLIDESHOW');
          }
        }}
        landingPageFlavor={landingPageFlavor}
        showSignInNext={hasCompletedOnboardingSlideshow}
      ></Welcome>
    );
  } else if (onboardingStep === 'SLIDESHOW') {
    return (
      <OnboardingSlideshow
        next={() => {
          setOnboardingStep('SIGN_IN');
        }}
        nativeAppFunnelMode={false}
      />
    );
  } else {
    if (hasTokenAndUserInfoScopes && firstName) {
      if (failedGmailConnection) {
        return <MissingScopes></MissingScopes>;
      } else {
        return <ConnectGmailPrompt firstName={firstName} email={email}></ConnectGmailPrompt>;
      }
    } else {
      return <SignIn />;
    }
  }
}

export default OnboardingFlowWithSlideshow;
